import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, StatusBadge } from "@/components/app/common";
import { EntityPicker, customerRender } from "@/components/app/EntityPicker";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export const StubBanner = () => (
  <div className="mb-5 flex gap-3 items-start rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900" data-testid="design-stub-banner">
    <FlaskConical className="h-5 w-5 shrink-0 mt-0.5" />
    <div className="text-sm"><span className="font-semibold">Initial version (v0.1).</span> Design records, revisions, estimate header, BOM placeholder and documents only. Heat-load calculation and machine selection come in the full Design Hub.</div>
  </div>
);

const orderRender = (o) => ({ title: o.order_no, subtitle: `${o.customer_snapshot?.name} · ${o.status}` });

function NewDesign({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ title: "", application: "", notes: "" });
  const [customer, setCustomer] = useState(null);
  const [order, setOrder] = useState(null);
  const save = async () => {
    if (!customer) return toast.error("Pick a customer");
    try {
      const r = await api.post("/designs", { ...f, customer_id: customer.id, order_id: order?.id || null });
      toast.success(`${r.data.design_no} created`);
      navigate(`/designs/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="new-design-dialog">
        <DialogHeader><DialogTitle>New design record</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. 2-tonne cold room, Pune DC" data-testid="design-title-input" /></div>
          <div><Label>Customer *</Label><EntityPicker url="/customers?active=true" value={customer} onChange={(c) => { setCustomer(c); setOrder(null); }} render={customerRender} placeholder="Search customer" testid="design-customer-picker" /></div>
          {customer && <div><Label>Related order / opportunity (optional)</Label><EntityPicker url={`/orders?customer_id=${customer.id}`} value={order} onChange={setOrder} render={orderRender} placeholder="Search order" testid="design-order-picker" /></div>}
          <div><Label>Application</Label><Input value={f.application} onChange={(e) => setF({ ...f, application: e.target.value })} placeholder="e.g. Dairy, pharma, reefer" data-testid="design-application-input" /></div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="design-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="design-create-btn">Create design</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Designs() {
  const { can } = useMe();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data } = useFetch(`/designs?q=${encodeURIComponent(q)}`);
  return (
    <div data-testid="designs-page">
      <PageHeader title="Design Hub" subtitle="Design records and revisions that orders can reference">
        {can("write") && <Button onClick={() => setOpen(true)} data-testid="new-design-btn"><Plus className="h-4 w-4 mr-1" />New design</Button>}
      </PageHeader>
      <StubBanner />
      <Input placeholder="Search design no, title, customer…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm mb-3 bg-white" data-testid="designs-search-input" />
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="th text-left"><th className="px-3 py-2.5">Design</th><th className="px-3 py-2.5">Title</th><th className="px-3 py-2.5">Customer</th><th className="px-3 py-2.5">Current rev</th><th className="px-3 py-2.5">Latest approved</th><th className="px-3 py-2.5">Updated</th><th className="px-3 py-2.5">Status</th></tr></thead>
          <tbody>
            {(data || []).map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/designs/${d.id}`)} data-testid={`design-row-${d.design_no}`}>
                <td className="px-3 py-2.5 font-mono text-sky-700">{d.design_no}</td>
                <td className="px-3 py-2.5">{d.title}</td>
                <td className="px-3 py-2.5">{d.customer_snapshot.name}</td>
                <td className="px-3 py-2.5 font-mono">R{d.current_revision_no}</td>
                <td className="px-3 py-2.5 font-mono">{d.latest_approved_revision_no != null ? `R${d.latest_approved_revision_no}` : "—"}</td>
                <td className="px-3 py-2.5 text-xs">{fmtDate(d.updated_at)}</td>
                <td className="px-3 py-2.5"><StatusBadge status={d.status} /></td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">No design records yet</td></tr>}
          </tbody>
        </table>
      </div>
      <NewDesign open={open} onOpenChange={setOpen} />
    </div>
  );
}
